var _ViedocCheckbox_internals;
import { __classPrivateFieldGet, __classPrivateFieldSet, __decorate } from "tslib";
import 'element-internals-polyfill';
import { html, css, LitElement } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { customElement, property, query } from 'lit/decorators.js';
import { DisplayMode } from './enums/display-mode.js';
import { Size } from './enums/size.js';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { Large, ...CheckboxSize } = Size;
export { CheckboxSize };
export { DisplayMode };
let ViedocCheckbox = class ViedocCheckbox extends LitElement {
    constructor() {
        super();
        this.checked = false;
        this.disabled = false;
        this.indeterminate = false;
        this.name = '';
        this.mode = DisplayMode.Checkbox;
        this.size = Size.Medium;
        this.value = 'on';
        _ViedocCheckbox_internals.set(this, void 0);
        __classPrivateFieldSet(this, _ViedocCheckbox_internals, this.attachInternals(), "f");
        __classPrivateFieldGet(this, _ViedocCheckbox_internals, "f").role = 'checkbox';
    }
    willUpdate(changedProperties) {
        if (changedProperties.has('checked')) {
            __classPrivateFieldGet(this, _ViedocCheckbox_internals, "f").ariaChecked = this.checked.toString();
            __classPrivateFieldGet(this, _ViedocCheckbox_internals, "f").setFormValue(this.checked ? this.value : null);
        }
    }
    handleChange() {
        this.checked = this.inputElement.checked;
        this.dispatchEvent(new Event('change', {
            bubbles: true,
            composed: true,
        }));
    }
    renderSvg() {
        if (this.checked) {
            return html `
        <svg viewBox="0 0 24 24">
          <rect width="24" height="24" rx="4" fill="var(--primary-color)" />
          <path
            d="M10.219 14.561 7.355 11.803 6.4 12.722 10.219 16.4 18.4 8.519 17.446 7.6 10.219 14.561Z"
            fill="var(--primary-contrast-color)"
          />
        </svg>
      `;
        }
        if (this.indeterminate) {
            return html ` <svg viewBox="0 0 25 25">
        <rect width="25" height="25" rx="4" fill="var(--secondary-color)" />
        <path
          d="M19.7923 13.5418H5.20898V11.4585H19.7923V13.5418Z"
          fill="var(--secondary-contrast-color)"
        />
      </svg>`;
        }
        return html `
      <svg viewBox="0 0 24 24">
        <rect
          x="1"
          y="1"
          width="22"
          height="22"
          rx="4"
          fill="none"
          stroke="var(--color-gray-30)"
        />
      </svg>
    `;
    }
    renderToggleMode() {
        const toggleClass = classMap({
            switch: true,
            'switch--checked': this.checked,
        });
        return html `<span class="${toggleClass}"></span>`;
    }
    renderByMode(mode) {
        if (mode.toLowerCase() === DisplayMode.Toggle) {
            return this.renderToggleMode();
        }
        return this.renderSvg();
    }
    render() {
        const classNames = classMap({
            checkbox: true,
            'checkbox--disabled': this.disabled,
            [`checkbox--${this.size}`]: true,
        });
        return html `
      <label class=${classNames}>
        <input
          ?checked=${this.checked}
          ?disabled=${this.disabled}
          ?indeterminate=${this.indeterminate}
          name=${this.name}
          type="checkbox"
          .value=${this.value}
          @change=${this.handleChange}
        />
        ${this.renderByMode(this.mode)}
        <span class="text"><slot></slot></span>
      </label>
    `;
    }
};
_ViedocCheckbox_internals = new WeakMap();
ViedocCheckbox.formAssociated = true;
ViedocCheckbox.styles = [
    css `
      :host {
        display: inline-flex;
      }

      .checkbox {
        --checkbox-size: 1.5625rem;
        --checkbox-font-size: var(--font-size-sm);
        --checkbox-line-height: 1.3;
        --switch-height: 1.5em;

        display: flex;
        position: relative;
        align-items: flex-start;
      }

      .checkbox--small {
        --checkbox-size: 1.313rem;
        --checkbox-font-size: var(--font-size-xs);
      }

      input {
        position: absolute;
        opacity: 0;
        margin: 0;
        font-size: inherit;
        width: var(--checkbox-size);
        height: var(--checkbox-size);
        cursor: pointer;
      }

      input:focus-visible ~ svg {
        border-radius: 4px;
        outline-offset: 2px;
        outline: 5px auto Highlight; /* Firefox */
        outline: 5px auto -webkit-focus-ring-color; /* Chrome and Safari */
      }

      .text {
        font-family: var(--font-family-monument);
        font-size: var(--checkbox-font-size);
        line-height: var(--checkbox-line-height);
        color: var(--primary-text-color);
        /* Center align first line of text with checkbox */
        transform: translateY(
          calc(
            var(--checkbox-size) / 2 -
              (var(--checkbox-font-size) * var(--checkbox-line-height) / 2)
          )
        );
      }

      svg {
        width: var(--checkbox-size);
        height: var(--checkbox-size);
        margin-inline-end: 0.5em;
        flex-shrink: 0;
      }

      .checkbox--disabled {
        cursor: default;
      }

      .checkbox--disabled svg,
      .checkbox--disabled .switch {
        opacity: 0.5;
      }

      /* Display as switch */
      .switch {
        position: relative;
        inset: 0;
        background-color: var(--color-gray-30);
        transition: background-color 0.4s;
        height: var(--switch-height);
        min-width: 2.5em;
        border-radius: 2.125em;
        margin-inline-end: 0.5em;
      }

      .switch:before {
        position: absolute;
        content: '';
        height: 1.1em;
        width: 1.1em;
        left: 0.25em;
        bottom: 0.1995em;
        background-color: var(--success-contrast-color);
        transition: transform 0.4s;
        border-radius: 50%;
      }

      .switch--checked {
        background-color: var(--success-color);
      }

      .switch--checked:before {
        transform: translateX(0.9em);
      }

      input:focus-visible ~ .switch {
        outline-offset: 2px;
        outline: 5px auto Highlight; /* Firefox */
        outline: 5px auto -webkit-focus-ring-color; /* Chrome and Safari */
      }

      .switch ~ .text {
        transform: translateY(
          calc(
            var(var(--switch-height)) / 2 -
              (var(--checkbox-font-size) * var(--checkbox-line-height) / 2)
          )
        );
      }
    `,
];
__decorate([
    query('input')
], ViedocCheckbox.prototype, "inputElement", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocCheckbox.prototype, "checked", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocCheckbox.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], ViedocCheckbox.prototype, "indeterminate", void 0);
__decorate([
    property({ type: String, reflect: true })
], ViedocCheckbox.prototype, "name", void 0);
__decorate([
    property({ type: String, reflect: true, attribute: 'display-mode' })
], ViedocCheckbox.prototype, "mode", void 0);
__decorate([
    property({ type: String, reflect: true })
], ViedocCheckbox.prototype, "size", void 0);
__decorate([
    property({ type: String })
], ViedocCheckbox.prototype, "value", void 0);
ViedocCheckbox = __decorate([
    customElement('viedoc-checkbox')
], ViedocCheckbox);
export { ViedocCheckbox };
